import React, { useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { collection, doc, getDocs, query, where, updateDoc, addDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import EditableFeedback from './EditableFeedback';

const TeacherLessonsTab = () => {
  const [students, setStudents] = useState([]);
  const [expandedStudentId, setExpandedStudentId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = getAuth().onAuthStateChanged(async (user) => {
      if (!user) {
        setStudents([]);
        setLoading(false);
        return;
      }

      try {
        const q = query(
          collection(db, 'users'),
          where('role', '==', 'student'),
          where('assignedTo', 'array-contains', user.uid)
        );
        const snapshot = await getDocs(q);

        const userList = await Promise.all(snapshot.docs.map(async docSnap => {
          const data = docSnap.data();
          const id = docSnap.id;

          const writingsSnap = await getDocs(collection(db, 'users', id, 'writings'));
          const lessonsSnap = await getDocs(collection(db, 'users', id, 'lessons'));

          return {
            id,
            name: data.name || 'Unnamed Student',
            email: data.email || '',
            submissions: writingsSnap.docs.map(d => ({ lessonId: d.id, ...d.data() })),
            quizSubmissions: lessonsSnap.docs.map(d => ({ lessonId: d.id, ...d.data() }))
          };
        }));

        setStudents(userList);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleSaveFeedback = async (studentId, writingId, newFeedback) => {
    try {
      const ref = doc(db, 'users', studentId, 'writings', writingId);
      const lessonNumber = writingId.replace(/\D/g, '');
      await updateDoc(ref, { feedback: newFeedback, updatedAt: new Date() });

      const notifRef = collection(db, 'users', studentId, 'notifications');
      await addDoc(notifRef, {
        type: 'feedback',
        message: `New feedback available for Lesson ${lessonNumber}.`,
        createdAt: new Date(),
        read: false,
        target: { type: 'lesson', lessonId: writingId }
      });

      alert('Feedback successfully saved!');
    } catch (err) {
      console.error(err);
      alert('Error saving feedback.');
    }
  };

  if (loading) return <div style={styles.loading}>Gathering student results...</div>;

  return (
    <div style={styles.container}>
      {students.length === 0 ? (
        <div style={styles.emptyState}>No students currently assigned to you.</div>
      ) : (
        students.map(student => (
          <div key={student.id} style={styles.studentCard}>
            <div style={styles.cardHeader} onClick={() => setExpandedStudentId(expandedStudentId === student.id ? null : student.id)}>
              <div>
                <h3 style={styles.studentName}>{student.name}</h3>
                <span style={styles.studentEmail}>{student.email}</span>
              </div>
              <div style={styles.statusGroup}>
                <span style={styles.countBadge}>Lessons: {student.quizSubmissions.length}</span>
                <button style={styles.expandBtn}>{expandedStudentId === student.id ? 'Close' : 'View Progress'}</button>
              </div>
            </div>

            {expandedStudentId === student.id && (
              <div style={styles.detailsArea}>
                {[...new Set([...student.submissions.map(s => s.lessonId), ...student.quizSubmissions.map(q => q.lessonId)])]
  .sort((a, b) => {
    // 🔹 Extract numbers from the strings (e.g., 'lesson10' becomes 10)
    const numA = parseInt(a.replace(/\D/g, ''), 10);
    const numB = parseInt(b.replace(/\D/g, ''), 10);
    return numA - numB; // Sort them as numbers
  })
  .map(lid => {
    // ... rest of your lesson block mapping
                    const writing = student.submissions.find(s => s.lessonId === lid);
                    const quiz = student.quizSubmissions.find(q => q.lessonId === lid);

                    return (
                      <div key={lid} style={styles.lessonBlock}>
                        <h4 style={styles.lessonTitle}>Lesson: {lid.replace('lesson', '')}</h4>

                        <div style={styles.gridRow}>
                          {/* 1. QUIZ PROGRESS */}
                          <div style={styles.subSection}>
                            <h5><span role="img" aria-label="quiz">📖</span> Quiz Analysis</h5>
                            {quiz?.readingPassage?.questions ? (
                              <div style={styles.historyList}>
                                {quiz.readingPassage.questions.map((q, i) => (
                                  <div key={i} style={styles.historyItem}>
                                    <div style={{ ...styles.statusDot, backgroundColor: q.isCorrect ? '#10b981' : '#ef4444' }} />
                                    <div style={styles.historyText}>
                                      <strong>Q{q.questionId}:</strong> {Array.isArray(q.userAnswer) ? q.userAnswer.join(', ') : q.userAnswer}
                                      {q.attemptHistory && q.attemptHistory.length > 1 && (
                                        <div style={styles.historyDetail}>
                                          History: {q.attemptHistory.join(' → ')}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : <span style={styles.dimText}>No quiz data</span>}
                          </div>

                          {/* 2. C-TEST COMPARISON */}
                          <div style={styles.subSection}>
                            <h5><span role="img" aria-label="vocab">🧠</span> Vocab Recall Analysis</h5>
                            {writing?.vocabRecall ? (
                              <div style={styles.cTestDisplay}>
                                {writing.vocabRecall.originalText ? (
                                  writing.vocabRecall.originalText.split('\n').map((para, pIdx) => (
                                    <p key={pIdx} style={styles.cTestPara}>
                                      {para.split(/(\w+\[\w+\])/g).map((part, wIdx) => {
                                        const match = part.match(/(\w+)\[(\w+)\]/);
                                        if (match) {
                                          const prefix = match[1];
                                          const suffix = match[2];
                                          const wordKey = suffix.toLowerCase();
                                          const userAns = writing.vocabRecall.answers?.[wordKey] || '___';
                                          const isCorrect = userAns.toLowerCase() === suffix.toLowerCase();
                                          const mistakes = writing.vocabRecall.mistakeCount?.[wordKey] || 0;
                                          const hints = writing.vocabRecall.hintsCount?.[wordKey] || 0;

                                          return (
                                            <span key={wIdx}>
                                              <strong>{prefix}</strong>
                                              <span style={{ color: isCorrect ? '#059669' : '#dc2626', fontWeight: 'bold' }}>
                                                [{userAns}]
                                              </span>
                                              {(mistakes > 0 || hints > 0) && (
                                                <sub style={{ color: '#f59e0b', marginLeft: '2px', fontSize: '0.7rem' }}>
                                                  ({mistakes > 0 ? `Err: ${mistakes}` : ''}{mistakes > 0 && hints > 0 ? ' | ' : ''}{hints > 0 ? `💡: ${hints}` : ''})
                                                </sub>
                                              )}
                                            </span>
                                          );
                                        }
                                        return <span key={wIdx}>{part}</span>;
                                      })}
                                    </p>
                                  ))
                                ) : (
                                  <p style={styles.dimText}>
                                    Words: {Object.entries(writing.vocabRecall.answers || {}).map(([k, v]) => `${k}: ${v}`).join(', ')}
                                  </p>
                                )}
                              </div>
                            ) : <span style={styles.dimText}>Not started</span>}
                          </div>
                        </div>

                        {/* 3. WRITING SUBMISSION */}
                        <div style={styles.writingSection}>
                          <h5><span role="img" aria-label="pencil">✍️</span> Writing Response</h5>
                          {writing?.writingText ? (
                            <div style={styles.writingContent}>
                              <div style={styles.essayText}>{writing.writingText}</div>
                              <div style={styles.feedbackArea}>
                                <p style={styles.label}>Teacher Feedback:</p>
                                <EditableFeedback
                                  initialContent={writing.feedback || ''}
                                  onSave={(content) => handleSaveFeedback(student.id, writing.lessonId, content)}
                                />
                              </div>
                            </div>
                          ) : <span style={styles.dimText}>No writing submitted yet.</span>}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
};

const styles = {
  container: { backgroundColor: '#f8fafc', padding: '20px', minHeight: '100vh' },
  loading: { textAlign: 'center', padding: '50px', color: '#64748b', fontSize: '1.2rem' },
  emptyState: { textAlign: 'center', padding: '40px', color: '#64748b' },
  studentCard: { backgroundColor: '#fff', borderRadius: '12px', marginBottom: '15px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', overflow: 'hidden' },
  cardHeader: { padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' },
  studentName: { margin: 0, color: '#1e3a8a', fontSize: '1.25rem' },
  studentEmail: { color: '#64748b', fontSize: '0.9rem' },
  statusGroup: { display: 'flex', alignItems: 'center', gap: '20px' },
  countBadge: { backgroundColor: '#eff6ff', color: '#1e40af', padding: '4px 12px', borderRadius: '20px', fontWeight: 'bold', fontSize: '0.85rem' },
  expandBtn: { border: 'none', background: 'none', color: '#3b82f6', fontWeight: 'bold', cursor: 'pointer' },
  detailsArea: { padding: '20px', backgroundColor: '#fcfcfc', borderTop: '1px solid #f1f5f9' },
  lessonBlock: { backgroundColor: '#fff', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '10px', marginBottom: '20px' },
  lessonTitle: { margin: '0 0 15px 0', borderBottom: '2px solid #3b82f6', display: 'inline-block', paddingRight: '20px' },
  gridRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' },
  subSection: { backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px', overflowY: 'auto', maxHeight: '300px' },
  historyList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  historyItem: { display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '0.9rem' },
  statusDot: { width: '10px', height: '10px', borderRadius: '50%', marginTop: '5px', flexShrink: 0 },
  historyDetail: { fontSize: '0.8rem', color: '#f59e0b', fontStyle: 'italic' },
  cTestDisplay: { fontSize: '0.9rem', lineHeight: '1.6', color: '#334155' },
  cTestPara: { marginBottom: '10px' },
  writingSection: { borderTop: '1px solid #f1f5f9', paddingTop: '20px' },
  essayText: { backgroundColor: '#fff', padding: '15px', borderLeft: '4px solid #3b82f6', borderRadius: '4px', color: '#334155', lineHeight: '1.6', marginBottom: '15px', fontSize: '1rem' },
  feedbackArea: { marginTop: '20px' },
  label: { fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '8px', color: '#1e3a8a' },
  dimText: { color: '#94a3b8', fontStyle: 'italic' }
};

export default TeacherLessonsTab;